// PersonnelSelectionPopup.tsx - Simplified Enterprise-Ready Personnel Selection
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PersonnelSelectionPopup.css';
import '../companyManagement/CompanyFormOverrides.css';
import { PersonnelManagementWrapper } from '../PersonnelManagement';
import { BulkPersonnelUpload } from '../docAI/BulkPersonnelUpload';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

interface Personnel {
  personnelId: number;
  companyId: number;
  firstName: string; // Added for better identification
  lastName: string;  // Added for better identification
  nationalIdNo: string;
  dateOfBirth: string;
  nationality: string;
  typeOfVisa: string;
  visaExpiry: string;
  bloodGroup: string;
  photoFileName?: string;
  photoFileUrl?: string;
  photoFileSize?: number;
  documentFileName?: string;
  documentFileUrl?: string;
  documentFileSize?: number;
  companyName?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PersonnelSelectionPopupProps {
  isOpen: boolean;
  selectedPersonnel: Personnel[];
  getAccessToken: () => Promise<string>;
  onClose: () => void;
  onConfirm: (personnel: Personnel[]) => void;
  onStatusUpdate: (status: { type: 'success' | 'error' | null; message: string }) => void;
}

type SortField = 'firstName' | 'lastName' | 'nationalIdNo' | 'nationality' | 'visaExpiry';
type SortDirection = 'asc' | 'desc';

export const PersonnelSelectionPopup: React.FC<PersonnelSelectionPopupProps> = ({
  isOpen,
  selectedPersonnel,
  getAccessToken,
  onClose,
  onConfirm,
  onStatusUpdate
}) => {
  // State management
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [tempSelectedPersonnel, setTempSelectedPersonnel] = useState<Personnel[]>([]);
  
  // Loading states
  const [isLoadingPersonnel, setIsLoadingPersonnel] = useState(false);
  
  // Add-new personnel inline creation state
  const [showAddNew, setShowAddNew] = useState(false);

  // Bulk upload (Excel + ZIP) state
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Help panel state
  const [showHelp, setShowHelp] = useState(true);

  // Track newly created personnel ID for auto-selection after list refresh
  const pendingAutoSelectId = useRef<number | null>(null);
  // Track bulk-created personnel IDs for auto-selection after list refresh
  const pendingAutoSelectIds = useRef<number[]>([]);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [viewFilter, setViewFilter] = useState<'all' | 'selected'>('all');
  
  // Sorting states
  const [sortField, setSortField] = useState<SortField>('firstName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [revealedRow, setRevealedRow] = useState<string | null>(null);
  
  // Refs
  const modalRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Helper functions
  const maskNationalId = (id: string): string => {
    if (!id || id.length < 5) return id;
    return id.substring(0, 2) + '**' + id.substring(id.length - 2);
  };

  const toTitleCase = (str: string): string => {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getFullName = (person: Personnel): string => {
    const first = toTitleCase(person.firstName || '');
    const last = toTitleCase(person.lastName || '');
    return `${first} ${last}`.trim() || 'Unknown Name';
  };

  // Handle cancel - moved up and memoized to fix dependency warning
  const handleCancel = useCallback(() => {
    setTempSelectedPersonnel([...selectedPersonnel]);
    onClose();
  }, [selectedPersonnel, onClose]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount or close
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Focus search input after drawer animation completes (Safari fix)
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Enhanced wheel event handling for the table scroll area
  const handleTableWheel = useCallback((e: React.WheelEvent) => {
    const scrollContainer = tableScrollRef.current;
    if (!scrollContainer) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight;
    
    // Only prevent propagation if we're at scroll boundaries
    if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  // Handle modal overlay clicks and escape key - fixed dependency array
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleCancel]);

  // Load personnel from API
  const loadPersonnel = useCallback(async () => {
    setIsLoadingPersonnel(true);
    try {
      const accessToken = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/personnel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Mock firstName and lastName if not available in API
          const enrichedPersonnel = data.data.map((person: Personnel) => ({
            ...person,
            firstName: person.firstName || `Person`,
            lastName: person.lastName || person.nationalIdNo.substring(0, 3)
          }));
          setPersonnel(enrichedPersonnel);
        } else {
          onStatusUpdate({
            type: 'error',
            message: data.message || 'Failed to load personnel'
          });
        }
      } else {
        const errorText = await response.text();
        console.error('Error loading personnel:', response.status, errorText);
        
        let userMessage;
        if (response.status === 401) {
          userMessage = 'Authentication failed. Please sign in again.';
        } else if (response.status === 403) {
          userMessage = 'Access denied. You do not have permission to view personnel records.';
        } else {
          userMessage = `Failed to load personnel (${response.status})`;
        }
        
        onStatusUpdate({
          type: 'error',
          message: userMessage
        });
      }
    } catch (error) {
      console.error('Error loading personnel:', error);
      onStatusUpdate({
        type: 'error',
        message: 'Error loading personnel. Please try again.'
      });
    } finally {
      setIsLoadingPersonnel(false);
    }
  }, [getAccessToken, onStatusUpdate]);

  // Initialize when popup opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedPersonnel([...selectedPersonnel]);
      setSearchQuery('');
      setViewFilter('all');
      setShowAddNew(false);
      setShowBulkUpload(false);
      loadPersonnel();
    }
  }, [isOpen, selectedPersonnel, loadPersonnel]);

  // Auto-select newly created personnel once the refreshed list includes them
  useEffect(() => {
    if (pendingAutoSelectId.current !== null && personnel.length > 0) {
      const newPerson = personnel.find(p => p.personnelId === pendingAutoSelectId.current);
      if (newPerson) {
        // Add to selection if not already selected
        setTempSelectedPersonnel(prev => {
          const alreadySelected = prev.some(p => p.personnelId === newPerson.personnelId);
          return alreadySelected ? prev : [...prev, newPerson];
        });
        pendingAutoSelectId.current = null; // clear once handled
      }
    }
  }, [personnel]);

  // Auto-select bulk-imported personnel once the refreshed list includes them
  useEffect(() => {
    if (pendingAutoSelectIds.current.length > 0 && personnel.length > 0) {
      const ids = pendingAutoSelectIds.current;
      const toAdd = personnel.filter(p => ids.includes(p.personnelId));
      if (toAdd.length > 0) {
        setTempSelectedPersonnel(prev => {
          const existingIds = new Set(prev.map(p => p.personnelId));
          const additions = toAdd.filter(p => !existingIds.has(p.personnelId));
          return additions.length ? [...prev, ...additions] : prev;
        });
        pendingAutoSelectIds.current = []; // clear once handled
      }
    }
  }, [personnel]);

  // Filter and sort personnel
  const filteredAndSortedPersonnel = personnel
    .filter(person => {
      // View filter - show all or only selected
      if (viewFilter === 'selected') {
        const isSelected = tempSelectedPersonnel.some(p => p.personnelId === person.personnelId);
        if (!isSelected) return false;
      }

      // Search filter
      const query = searchQuery.toLowerCase();
      const matchesSearch = !query || 
        getFullName(person).toLowerCase().includes(query) ||
        person.nationality.toLowerCase().includes(query) ||
        maskNationalId(person.nationalIdNo).toLowerCase().includes(query) ||
        person.nationalIdNo.substring(0, 2).toLowerCase().includes(query);

      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortField) {
        case 'firstName':
          aValue = a.firstName || '';
          bValue = b.firstName || '';
          break;
        case 'lastName':
          aValue = a.lastName || '';
          bValue = b.lastName || '';
          break;
        case 'nationalIdNo':
          aValue = a.nationalIdNo;
          bValue = b.nationalIdNo;
          break;
        case 'nationality':
          aValue = a.nationality;
          bValue = b.nationality;
          break;
        case 'visaExpiry':
          aValue = a.visaExpiry ? new Date(a.visaExpiry).getTime() : 0;
          bValue = b.visaExpiry ? new Date(b.visaExpiry).getTime() : 0;
          break;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        const comparison = (aValue as number) - (bValue as number);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle row selection - Updated to always toggle (multi-select)
  const handleRowSelect = (person: Personnel) => {
    const isSelected = tempSelectedPersonnel.some(p => p.personnelId === person.personnelId);
    
    if (isSelected) {
      // Remove from selection
      setTempSelectedPersonnel(prev => prev.filter(p => p.personnelId !== person.personnelId));
    } else {
      // Add to selection
      setTempSelectedPersonnel(prev => [...prev, person]);
    }
  };

  // Handle checkbox toggle - Same behavior as row click now
  const handleCheckboxToggle = (person: Personnel) => {
    handleRowSelect(person);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onConfirm(tempSelectedPersonnel);
    onClose();
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };

  // Handle inline personnel creation completed — refresh list and close form
  const handleAddNewComplete = () => {
    setShowAddNew(false);
    loadPersonnel(); // refresh the list to include newly created personnel
  };

  // Handle newly created personnel — store ID for auto-selection after list refreshes
  const handleNewPersonnelCreated = (personnelData: any) => {
    if (personnelData?.personnelId) {
      pendingAutoSelectId.current = personnelData.personnelId;
    }
  };

  // Handle bulk import completion — auto-select all created personnel, refresh, close
  const handleBulkComplete = (createdPersonnel: any[]) => {
    const ids = (createdPersonnel || [])
      .map(p => p?.personnelId)
      .filter((id): id is number => typeof id === 'number');
    if (ids.length > 0) {
      pendingAutoSelectIds.current = ids;
    }
    setShowBulkUpload(false);
    loadPersonnel(); // refresh list to include newly imported personnel
  };

 

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content personnel-selection-modal enterprise-modal" onClick={(e) => e.stopPropagation()} ref={modalRef}>
        
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-title-section">
            <span className="modal-title-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </span>
            <h3>Select Personnel</h3>
            {personnel.length > 0 && (
              <span className="personnel-count-badge">{personnel.length} Available</span>
            )}
          </div>
          <button onClick={handleCancel} className="close-modal-btn" title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content row: main + help panel */}
        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* Left: main content */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Search Bar + Add New — outside modal-body to avoid overflow:hidden focus issues */}
        <div className="modal-search-bar">
          <div className="search-and-add-row">
            <div className="search-filter-bar">
              <div className="search-input-container">
                <svg className="search-icon-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by name, nationality, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                  autoComplete="off"
                />
                {searchQuery && (
                  <button onClick={clearSearch} className="clear-search-btn" title="Clear">×</button>
                )}
              </div>
              <select
                value={viewFilter}
                onChange={(e) => setViewFilter(e.target.value as 'all' | 'selected')}
                className="view-filter"
              >
                <option value="all">All</option>
                <option value="selected">Selected only</option>
              </select>
            </div>
            <div className="add-new-inline">
              <span className="add-new-hint">Can't find?</span>
              <button
                type="button"
                onClick={() => setShowAddNew(true)}
                className="add-new-personnel-btn"
                title="Add new personnel"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
                Add New
              </button>
              <button
                type="button"
                onClick={() => setShowBulkUpload(true)}
                className="add-new-personnel-btn bulk-variant"
                title="Bulk upload pass holders from a single ZIP (Excel + documents)"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Bulk Upload
              </button>
            </div>
          </div>
        </div>

        {/* Modal Body — table only */}
        <div className="modal-body">

          {/* Personnel Table Container - Now with maximum available space */}
          <div className="table-container">
            {isLoadingPersonnel ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading personnel records...</p>
              </div>
            ) : filteredAndSortedPersonnel.length > 0 ? (
              <div className="table-scroll-wrapper" ref={tableScrollRef} onWheel={handleTableWheel}>
                <table className="personnel-table">
                  <thead>
                    <tr>
                      <th className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={tempSelectedPersonnel.length === filteredAndSortedPersonnel.length && filteredAndSortedPersonnel.length > 0}
                          onChange={() => {
                            if (tempSelectedPersonnel.length === filteredAndSortedPersonnel.length) {
                              setTempSelectedPersonnel([]);
                            } else {
                              setTempSelectedPersonnel([...filteredAndSortedPersonnel]);
                            }
                          }}
                          className="select-all-checkbox"
                        />
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('firstName')}
                      >
                        Name
                        {sortField === 'firstName' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('nationalIdNo')}
                      >
                        ID
                        {sortField === 'nationalIdNo' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('nationality')}
                      >
                        Nationality
                        {sortField === 'nationality' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th>ID Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedPersonnel.map((person) => {
                      const isSelected = tempSelectedPersonnel.some(p => p.personnelId === person.personnelId);
                      
                      return (
                        <tr
                          key={person.personnelId}
                          className={`personnel-row ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleRowSelect(person)}
                        >
                          {/* Checkbox Column */}
                          <td className="checkbox-column">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleCheckboxToggle(person)}
                              onClick={(e) => e.stopPropagation()} // Prevent double handling
                            />
                          </td>
                          
                          {/* Name Column */}
                          <td className="personnel-name">
                            <div className="name-container">
                              <span className="full-name" style={{ userSelect: 'none' as const }}>
                                {revealedRow === String(person.personnelId) ? '•'.repeat(getFullName(person).length) : getFullName(person)}
                              </span>
                              <span className="personnel-id-small">#{person.personnelId}</span>
                            </div>
                          </td>
                          
                          {/* ID Column */}
                          <td className="personnel-id">
                            <span
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              title="Press and hold to reveal"
                              onMouseDown={(e) => { e.stopPropagation(); e.currentTarget.textContent = person.nationalIdNo; setRevealedRow(String(person.personnelId)); }}
                              onMouseUp={(e) => { e.currentTarget.textContent = maskNationalId(person.nationalIdNo); setRevealedRow(null); }}
                              onMouseLeave={(e) => { e.currentTarget.textContent = maskNationalId(person.nationalIdNo); setRevealedRow(null); }}
                            >
                              {maskNationalId(person.nationalIdNo)}
                            </span>
                          </td>
                          
                          {/* Nationality Column */}
                          <td className="nationality">
                            {toTitleCase(person.nationality)}
                          </td>
                          
                          {/* Expiry Date Column */}
                          <td className="expiry-column">
                            {(() => {
                              const expiry = person.visaExpiry;
                              if (!expiry) return <span className="expiry-none">—</span>;
                              const expiryDate = new Date(expiry);
                              const isExpired = expiryDate < new Date();
                              const formatted = expiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                              return (
                                <span className={`expiry-badge ${isExpired ? 'expired' : 'valid'}`}>
                                  {isExpired && (
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                    </svg>
                                  )}
                                  {formatted}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <h4>No Personnel Found</h4>
                <p>
                  {viewFilter === 'selected' 
                    ? 'No personnel are currently selected.'
                    : searchQuery
                      ? 'No personnel match your current search criteria.'
                      : 'No personnel records are available. Please add personnel first.'
                  }
                </p>
                <div className="empty-state-actions">
                  {(searchQuery || viewFilter === 'selected') && (
                    <button 
                      onClick={() => { 
                        setSearchQuery(''); 
                        setViewFilter('all'); 
                      }} 
                      className="btn btn-secondary btn-sm"
                    >
                      Show All Personnel
                    </button>
                  )}
                  <button
                    onClick={() => setShowAddNew(true)}
                    className="btn btn-primary btn-sm"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add New Personnel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <div className="footer-info">
            {tempSelectedPersonnel.length > 0 ? (
              <span className="selection-count has-selection">
                {tempSelectedPersonnel.length} of {personnel.length} selected
              </span>
            ) : (
              <span className="selection-count">0 of {personnel.length} selected</span>
            )}
          </div>
          <div className="footer-actions">
            <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
            <button
              onClick={handleConfirm}
              className="btn btn-primary"
              disabled={tempSelectedPersonnel.length === 0}
            >
              Confirm Selection
            </button>
          </div>
        </div>

          </div>{/* end left column */}

          {/* Help Panel — single container, animated width */}
          <div style={{
            width: showHelp ? 240 : 38,
            minWidth: showHelp ? 240 : 38,
            background: '#fafbfc',
            borderLeft: '1px solid #e5e7eb',
            overflow: 'hidden',
            position: 'relative',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.3s ease, min-width 0.3s ease'
          }}>
            {/* Toggle strip — always visible on right edge */}
            <div
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? 'Hide Guide' : 'Show Guide'}
              style={{
                position: 'absolute', top: 0, right: 0, bottom: 0, width: 32,
                background: '#f3f4f6', borderLeft: '1px solid #e5e7eb',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'flex-start', paddingTop: 14, gap: 6,
                cursor: 'pointer', color: '#9ca3af', zIndex: 5
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showHelp
                  ? <polyline points="9 18 15 12 9 6"/>
                  : <polyline points="15 18 9 12 15 6"/>
                }
              </svg>
              <span style={{
                writingMode: 'vertical-rl', textOrientation: 'mixed',
                fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
                whiteSpace: 'nowrap', fontFamily: "'Segoe UI', sans-serif"
              }}>{showHelp ? 'Hide Guide' : 'Show Guide'}</span>
            </div>

            {/* Panel content — visible when open, hidden by overflow when collapsed */}
            <div style={{
              padding: '20px 50px 20px 18px',
              overflowY: 'auto',
              boxSizing: 'border-box' as const,
              minWidth: 240,
              opacity: showHelp ? 1 : 0,
              transition: 'opacity 0.2s ease'
            }}>
              <div className="help-panel-card help-active">
                <h4 className="help-card-title">Select Personnel</h4>
                <p className="help-card-description">
                  Choose the personnel to include in this gate pass request. Use the search bar to find individuals by name, nationality, or ID number.
                </p>
                <div className="help-card-tip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <span>Click any row or its checkbox to select. You can select multiple personnel at once.</span>
                </div>
              </div>

              <div className="help-panel-quickref">
                <h4 className="help-quickref-title">Quick Reference</h4>
                <ul className="help-quickref-list">
                  <li><span className="help-quickref-bullet"></span><span>Click column headers to sort the table</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Use "Selected only" filter to review your picks</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Use "Add New" if a person isn't in the system yet</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Expired visas are flagged with a warning icon</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>{/* end flex row */}

        {/* Inline Personnel Creation Overlay */}
        {showAddNew && (
          <div className="add-new-personnel-overlay" onClick={(e) => e.stopPropagation()}>
            <PersonnelManagementWrapper
              getAccessToken={getAccessToken}
              onPersonnelListUpdate={handleAddNewComplete}
              onPersonnelCreated={handleNewPersonnelCreated}
              onStepChange={(step) => {
                console.log('[PersonnelSelection] Inline creation step:', step);
              }}
            />
          </div>
        )}

        {/* Bulk Upload Overlay */}
        {showBulkUpload && (
          <div className="add-new-personnel-overlay" onClick={(e) => e.stopPropagation()}>
            <BulkPersonnelUpload
              getAccessToken={getAccessToken}
              onClose={() => setShowBulkUpload(false)}
              onComplete={handleBulkComplete}
              onStatusUpdate={onStatusUpdate}
              usage="request"
            />
          </div>
        )}
      </div>
    </div>
  );
};